import {
  computeCashflow,
  tDayLabel,
  type Cashflow,
  type WeekBucket,
} from '../lib/cashflow';
import { formatMoney } from '../lib/currency';
import type { EventData, VatMode } from '../lib/types';
import CashflowChart from './CashflowChart';

type Props = {
  data: EventData;
  eventDateISO: string | null | undefined;
  currency: string;
  vatMode: VatMode;
};

export default function CashflowCard({ data, eventDateISO, currency, vatMode }: Props) {
  if (!eventDateISO) {
    return (
      <div className="card cashflow-card">
        <div className="section-head">
          <h2>Cashflow timeline</h2>
        </div>
        <div className="muted">
          Set the event date in the top bar to see when money actually moves.
        </div>
      </div>
    );
  }

  const cf = computeCashflow(data, eventDateISO);

  const empty = cf.weeks.length === 0;

  return (
    <div className="card cashflow-card">
      <div className="section-head">
        <h2>Cashflow timeline</h2>
        <span className="muted">
          Conservative view (min prices) · weekly buckets · amounts{' '}
          {vatMode === 'net' ? 'net of VAT' : 'gross'}
        </span>
      </div>

      {empty ? (
        <div className="muted">
          Add some revenue or costs to see the timeline.
        </div>
      ) : (
        <>
          <TroughCallout cashflow={cf} currency={currency} />
          <CashflowChart cashflow={cf} currency={currency} />
          <Milestones cashflow={cf} currency={currency} />
        </>
      )}
    </div>
  );
}

function TroughCallout({ cashflow, currency }: { cashflow: Cashflow; currency: string }) {
  if (!cashflow.trough) return null;
  const { amount, date, daysToEvent } = cashflow.trough;
  const negative = amount < 0;
  return (
    <div className={`cf-trough-callout ${negative ? 'bad' : 'good'}`}>
      <span className="cf-trough-icon">{negative ? '⚠' : '✓'}</span>
      <div>
        <div className="cf-trough-headline">
          {negative ? 'Lowest cash position: ' : 'Cash stays positive throughout — lowest point '}
          <strong className={negative ? 'neg' : 'pos'}>{formatMoney(amount, currency)}</strong>
        </div>
        <div className="cf-trough-sub">
          {negative
            ? `Week of ${date} (${tDayLabel(daysToEvent)}). You'll need at least this much working capital to bridge the gap.`
            : `Week of ${date} (${tDayLabel(daysToEvent)}). No working-capital gap based on the current plan.`}
        </div>
      </div>
    </div>
  );
}

function Milestones({ cashflow, currency }: { cashflow: Cashflow; currency: string }) {
  const rows = cashflow.weeks.filter((w) => w.movements.length > 0);
  if (rows.length === 0) return null;
  return (
    <div className="cf-milestones">
      <div className="cf-milestones-head">Key milestones (by week)</div>
      <div className="cf-milestones-grid">
        <div className="cf-milestones-col-head">Week</div>
        <div className="cf-milestones-col-head">Movement</div>
        <div className="cf-milestones-col-head right">Cumulative</div>
        {rows.map((w) => (
          <MilestoneRow key={w.weekStart} week={w} currency={currency} />
        ))}
      </div>
    </div>
  );
}

function MilestoneRow({ week, currency }: { week: WeekBucket; currency: string }) {
  const inMoves = week.movements.filter((m) => m.amount > 0);
  const outMoves = week.movements.filter((m) => m.amount < 0);

  return (
    <>
      <div className="cf-milestone-date">
        <div className="cf-milestone-week">{week.weekStart}</div>
        <div className="cf-milestone-tday">{tDayLabel(week.daysToEvent)}</div>
      </div>
      <div className="cf-milestone-detail">
        {inMoves.length > 0 && (
          <div className="cf-milestone-in">
            <strong>+{formatMoney(week.inflow, currency)}</strong>
            <span className="muted"> · {summarize(inMoves)}</span>
          </div>
        )}
        {outMoves.length > 0 && (
          <div className="cf-milestone-out">
            <strong>−{formatMoney(week.outflow, currency)}</strong>
            <span className="muted"> · {summarize(outMoves)}</span>
          </div>
        )}
      </div>
      <div className="cf-milestone-cumulative right">
        <span className={week.cumulative < 0 ? 'neg' : 'pos'}>
          {formatMoney(week.cumulative, currency)}
        </span>
      </div>
    </>
  );
}

function summarize(arr: { description: string }[]): string {
  // Each movement description is "Category · Item" (see cashflow.ts). Group
  // by category so a week with many line items stays readable without ever
  // hiding any of them — the row simply wraps to a second line if long.
  const groups = new Map<string, string[]>();
  for (const { description } of arr) {
    const sepIdx = description.indexOf(' · ');
    const category = sepIdx >= 0 ? description.slice(0, sepIdx) : description;
    const item = sepIdx >= 0 ? description.slice(sepIdx + 3) : '';
    let items = groups.get(category);
    if (!items) {
      items = [];
      groups.set(category, items);
    }
    if (item && !items.includes(item)) items.push(item);
  }

  const parts: string[] = [];
  for (const [category, items] of groups) {
    parts.push(items.length === 0 ? category : `${category}: ${items.join(', ')}`);
  }
  return parts.join('  ·  ');
}
